import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import socket from "../../../services/socket";

import OrderTabs from "../../../components/delivery/Orders/OrderTabs";
import OrderCard from "../../../components/delivery/Orders/OrderCard";
import EmptyOrders from "../../../components/delivery/Orders/EmptyOrders";

import "./Orders.css";

function Orders() {

    const [activeTab, setActiveTab] = useState("pending");

    const [orders, setOrders] = useState([]);

    const [loading, setLoading] = useState(true);

    const [refreshing, setRefreshing] = useState(false);

    // ============================================
    // Fetch Orders
    // ============================================

    const fetchOrders = async () => {

        try {

            if (!refreshing) {

                setLoading(true);

            }

            const [

                availableResponse,

                myOrdersResponse,

            ] = await Promise.all([

                API.get("/delivery/orders"),

                API.get("/delivery/my-orders"),

            ]);

            const availableOrders =

                availableResponse.data.success

                    ? availableResponse.data.orders

                    : [];

            const myOrders =

                myOrdersResponse.data.success

                    ? myOrdersResponse.data.orders

                    : [];

            // Remove duplicate orders

            const merged = [

                ...availableOrders,

                ...myOrders,

            ];

            const uniqueOrders = merged.filter(

                (order, index, self) =>

                    index ===

                    self.findIndex(

                        (o) => o._id === order._id

                    )

            );

            setOrders(uniqueOrders);

        }

        catch (error) {

            console.log(error);

        }

        finally {

            setLoading(false);

            setRefreshing(false);

        }

    };

    // ============================================
    // Initial Load
    // ============================================

    useEffect(() => {

        fetchOrders();

    }, []);

    // ============================================
    // Socket Events
    // ============================================

    useEffect(() => {

        socket.on("dashboard-refresh", () => {

            fetchOrders();

        });

        socket.on("order-updated", () => {

            fetchOrders();

        });

        socket.on("order-delivered", () => {

            fetchOrders();

        });

        socket.on(

            "delivery-request-accepted",

            (orderId) => {

                setOrders((previous) =>

                    previous.filter(

                        (order) =>

                            order._id !== orderId

                    )

                );

            }

        );

        return () => {

            socket.off("dashboard-refresh");

            socket.off("order-updated");

            socket.off("order-delivered");

            socket.off("delivery-request-accepted");

        };

    }, []);

    // ============================================
    // Refresh Button
    // ============================================

    const handleRefresh = () => {

        setRefreshing(true);

        fetchOrders();

    };

    // ============================================
    // Counts
    // ============================================

    const counts = useMemo(() => ({

        pending: orders.filter(

            (o) =>

                o.status === "Ready For Pickup"

        ).length,

        active: orders.filter(

            (o) =>

                [

                    "Assigned",

                    "Reached Restaurant",

                    "Picked Up",

                    "Out For Delivery",

                ].includes(o.status)

        ).length,

        completed: orders.filter(

            (o) =>

                o.status === "Delivered"

        ).length,

        cancelled: orders.filter(

            (o) =>

                o.status === "Rejected"

        ).length,

    }), [orders]);

    // ============================================
    // Filter Orders
    // ============================================

    const filteredOrders = useMemo(() => {

        switch (activeTab) {

            case "pending":

                return orders.filter(

                    (o) =>

                        o.status === "Ready For Pickup"

                );

            case "active":

                return orders.filter(

                    (o) =>

                        [

                            "Assigned",

                            "Reached Restaurant",

                            "Picked Up",

                            "Out For Delivery",

                        ].includes(o.status)

                );

            case "completed":

                return orders.filter(

                    (o) =>

                        o.status === "Delivered"

                );

            case "cancelled":

                return orders.filter(

                    (o) =>

                        o.status === "Rejected"

                );

            default:

                return [];

        }

    }, [orders, activeTab]);

    return (

        <div className="delivery-orders-page">

            <div className="orders-header">

                <div>

                    <h1>

                        Orders

                    </h1>

                    <p>

                        Manage your delivery orders.

                    </p>

                </div>

                <button

                    className="refresh-orders-btn"

                    onClick={handleRefresh}

                    disabled={refreshing}

                >

                    {

                        refreshing

                            ? "Refreshing..."

                            : "Refresh"

                    }

                </button>

            </div>

            <OrderTabs

                activeTab={activeTab}

                setActiveTab={setActiveTab}

                counts={counts}

            />

            {

                loading

                ? (

                    <div

                        style={{

                            textAlign: "center",

                            padding: "40px",

                        }}

                    >

                        Loading Orders...

                    </div>

                )

                : filteredOrders.length === 0

                ? (

                    <EmptyOrders

                        status={activeTab}

                        refresh={handleRefresh}

                    />

                )

                : (

                    <div className="orders-list">

                        {

                            filteredOrders.map((order) => (

                                <OrderCard

                                    key={order._id}

                                    order={order}

                                    refresh={fetchOrders}

                                />

                            ))

                        }

                    </div>

                )

            }

        </div>

    );

}

export default Orders;